import { BaseRoutingStrategy } from './BaseRoutingStrategy';

export class HereRoutingStrategy extends BaseRoutingStrategy {
  constructor(apiKey) {
    super(apiKey);
  }

  async calculateRoute({
    waypoints = [],
    avoidTolls = false,
    avoidHighways = false,
    avoidFerries = false,
    vehicle = null
  } = {}) {
    if (!this.apiKey) {
      throw new Error('HERE API key mancante');
    }

    const validWaypoints = waypoints
      .map(waypoint => {
        const lat = Number(waypoint.lat);
        const lng = Number(waypoint.lng);
        return Number.isFinite(lat) && Number.isFinite(lng)
          ? { ...waypoint, lat, lng }
          : null;
      })
      .filter(Boolean);

    if (validWaypoints.length < 2) {
      throw new Error('Origine o destinazione non valide');
    }

    const origin = validWaypoints[0];
    const destination = validWaypoints[validWaypoints.length - 1];
    const params = new URLSearchParams({
      apikey: this.apiKey,
      transportMode: vehicle ? 'truck' : 'car',
      routingMode: 'fast',
      origin: `${origin.lat},${origin.lng}`,
      destination: `${destination.lat},${destination.lng}`,
      return: 'polyline,summary'
    });

    validWaypoints.slice(1, -1).forEach(waypoint => {
      params.append('via', `${waypoint.lat},${waypoint.lng}`);
    });

    const avoidedFeatures = [];
    if (avoidTolls) avoidedFeatures.push('tollRoad');
    if (avoidHighways) avoidedFeatures.push('controlledAccessHighway');
    if (avoidFerries) avoidedFeatures.push('ferry');
    if (avoidedFeatures.length > 0) {
      params.set('avoid[features]', avoidedFeatures.join(','));
    }

    if (vehicle) {
      this.appendVehicleParameters(params, vehicle);
    }

    const response = await fetch(`https://router.hereapi.com/v8/routes?${params.toString()}`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result?.title || result?.cause || result?.action || `HERE Routing HTTP ${response.status}`
      );
    }

    const route = result.routes?.[0];
    if (!route) {
      throw new Error('HERE non ha restituito alcun percorso');
    }

    const distanceMeters = route.sections.reduce(
      (total, section) => total + (section.summary?.length || 0),
      0
    );
    const durationSeconds = route.sections.reduce(
      (total, section) => total + (section.summary?.duration || 0),
      0
    );
    const durationHours = Math.floor(durationSeconds / 3600);
    const durationMinutes = Math.floor((durationSeconds % 3600) / 60);

    return {
      provider: 'here',
      raw: route,
      route,
      distanceMeters,
      durationSeconds,
      distanceText: `${(distanceMeters / 1000).toFixed(1)} km`,
      durationText: durationHours > 0
        ? `${durationHours}h ${durationMinutes}m`
        : `${durationMinutes} min`
    };
  }

  appendVehicleParameters(params, vehicle) {
    const numericProperties = {
      height: 'height',
      width: 'width',
      length: 'length',
      currentWeight: 'current_weight',
      grossWeight: 'gross_weight',
      weightPerAxle: 'weight_per_axle',
      axleCount: 'axle_count',
      trailerCount: 'trailer_count',
      trailerLength: 'trailer_length',
      kpraLength: 'kpra_length',
      heightAboveFirstAxle: 'height_above_first_axle',
      emptyWeight: 'empty_weight',
      payloadCapacity: 'payload_capacity',
      trailerAxleCount: 'trailer_axle_count',
      tiresCount: 'tires_count'
    };

    Object.entries(numericProperties).forEach(([apiProperty, vehicleProperty]) => {
      const value = vehicle[vehicleProperty];
      if (value === null || value === undefined || value === '') return;

      const numericValue = Number(value);
      const isAxleCount = ['axleCount', 'trailerCount', 'trailerAxleCount'].includes(apiProperty);
      if (!Number.isFinite(numericValue) || numericValue < 0 || (isAxleCount && numericValue === 0)) return;

      params.set(`vehicle[${apiProperty}]`, String(numericValue));
    });

    const trailerAxleCount = Number(vehicle.trailer_axle_count);
    if (Number.isInteger(trailerAxleCount) && trailerAxleCount > 0) {
      const axleCount = Number(vehicle.axle_count);
      if (!Number.isInteger(axleCount) || trailerAxleCount >= axleCount) {
        throw new Error('Specifiche del veicolo non valide: gli assi del rimorchio devono essere inferiori agli assi totali');
      }

      const trailerCount = Number(vehicle.trailer_count);
      if (!Number.isInteger(trailerCount) || trailerCount < 1) {
        params.set('vehicle[trailerCount]', '1');
      }
    }

    const axleGroups = vehicle.weight_per_axle_group;
    if (axleGroups && typeof axleGroups === 'object') {
      const entries = Array.isArray(axleGroups)
        ? axleGroups.map(group => [group.type, group.weight])
        : Object.entries(axleGroups);
      const value = entries
        .filter(([type, weight]) => type && Number.isFinite(Number(weight)) && Number(weight) > 0)
        .map(([type, weight]) => `${type}:${Number(weight)}`)
        .join(',');

      if (value) {
        params.delete('vehicle[weightPerAxle]');
        params.set('vehicle[weightPerAxleGroup]', value);
      }
    }
  }

  async geocodeWaypoints(waypoints) {
    return new Promise((resolve) => {
      const needsGeocoding = waypoints.filter(wp => wp.needsGeocoding && wp.address);

      if (needsGeocoding.length === 0) {
        resolve();
        return;
      }

      let completed = 0;

      needsGeocoding.forEach(wp => {
        this._geocodeAddress(wp.address)
          .then(coords => {
            if (coords) {
              wp.lat = coords.lat;
              wp.lng = coords.lng;
              console.log('[HereRoutingStrategy] Geocoded:', wp.address, 'to', wp.lat, wp.lng);
            } else {
              console.warn('[HereRoutingStrategy] Geocoding failed:', wp.address);
            }
          })
          .catch(err => {
            console.error('[HereRoutingStrategy] Geocoding error:', wp.address, err);
          })
          .finally(() => {
            completed++;
            if (completed === needsGeocoding.length) {
              resolve();
            }
          });
      });
    });
  }

  async geocodeAddress(address) {
    return this._geocodeAddress(address);
  }

  async _geocodeAddress(address) {
    try {
      const url = `https://geocode.search.hereapi.com/v1/geocode`;
      const params = new URLSearchParams({
        q: address,
        apiKey: this.apiKey
      });

      const response = await fetch(`${url}?${params}`);
      const data = await response.json();

      if (data.items && data.items.length > 0) {
        const item = data.items[0];
        return {
          lat: item.position.lat,
          lng: item.position.lng
        };
      }

      return null;
    } catch (error) {
      console.error('[HereRoutingStrategy] Geocoding request error:', error);
      throw error;
    }
  }

  initAutocomplete(inputElement, onPlaceSelected) {
    if (!inputElement) return;

    let debounceTimer;
    let predictions = [];

    inputElement.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      const value = e.target.value;

      if (value.length < 2) {
        predictions = [];
        this._hidePredictions();
        return;
      }

      debounceTimer = setTimeout(async () => {
        try {
          predictions = await this._autocomplete(value);
          if (predictions.length > 0) {
            this._showPredictions(inputElement, predictions, (selected) => {
              this._getPlaceDetails(selected).then(place => {
                if (onPlaceSelected && place) {
                  onPlaceSelected(place);
                }
                this._hidePredictions();
                inputElement.value = '';
              });
            });
          } else {
            this._hidePredictions();
          }
        } catch (error) {
          console.error('[HereRoutingStrategy] Autocomplete error:', error);
        }
      }, 300);
    });

    inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });
  }

  _showPredictions(inputElement, predictions, onSelect) {
    let container = document.getElementById('here-autocomplete-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'here-autocomplete-container';
      container.style.cssText = 'position: absolute; z-index: 9999; background: white; border: 1px solid #ccc; max-height: 300px; overflow-y: auto; width: 100%; box-shadow: 0 2px 8px rgba(0,0,0,0.15);';
      inputElement.parentElement.style.position = 'relative';
      inputElement.parentElement.appendChild(container);
    }

    container.innerHTML = predictions.map((pred, idx) => `
      <div style="padding: 8px 12px; cursor: pointer; border-bottom: 1px solid #eee; transition: background-color 0.15s;" class="here-prediction-item" data-idx="${idx}">
        <div style="font-weight: bold;">${pred.title || 'Unknown'}</div>
        <div style="font-size: 0.85em; color: #666;">${pred.address?.label || pred.address?.countryName || ''}</div>
      </div>
    `).join('');

    container.style.display = 'block';

    const items = container.querySelectorAll('.here-prediction-item');
    items.forEach(el => {
      el.addEventListener('mouseover', () => {
        el.style.backgroundColor = '#f5f5f5';
      });
      el.addEventListener('mouseout', () => {
        el.style.backgroundColor = 'transparent';
      });
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt(el.dataset.idx);
        console.log('[HereRoutingStrategy] Selected prediction:', predictions[idx]);
        onSelect(predictions[idx]);
      });
    });
  }

  _hidePredictions() {
    const container = document.getElementById('here-autocomplete-container');
    if (container) {
      container.style.display = 'none';
    }
  }

  async _getPlaceDetails(prediction) {
    try {
        if (!prediction) {
            return null;
        }

        const address =
            prediction.address?.label ||
            prediction.title ||
            '';

        let coords = null;

        const lat = Number(prediction.position?.lat);
        const lng = Number(prediction.position?.lng);

        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            coords = { lat, lng };
        } else if (address) {
            coords = await this._geocodeAddress(address);
        }

        if (!coords) {
            return null;
        }

        return {
            name: prediction.title || address,
            formatted_address: address,
            geometry: {
                location: {
                    lat: () => coords.lat,
                    lng: () => coords.lng,
                },
            },
        };
    } catch (error) {
        console.error(
            '[HereRoutingStrategy] getPlaceDetails error:',
            error
        );

        return null;
    }
  }

  initOriginAutocomplete(inputElement, onPredictionsUpdated) {
    if (!inputElement) return;

    let debounceTimer;

    inputElement.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      const value = e.target.value;

      if (value.length < 2) {
        onPredictionsUpdated([]);
        return;
      }

      debounceTimer = setTimeout(async () => {
        try {
          const predictions = await this._autocomplete(value);
          onPredictionsUpdated(predictions || []);
        } catch (error) {
          console.error('[HereRoutingStrategy] Origin autocomplete error:', error);
          onPredictionsUpdated([]);
        }
      }, 300);
    });

    inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });

    inputElement.focus();
  }

  initDestinationAutocomplete(inputElement, onPredictionsUpdated) {
    if (!inputElement) return;

    let debounceTimer;

    inputElement.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      const value = e.target.value;

      if (value.length < 2) {
        onPredictionsUpdated([]);
        return;
      }

      debounceTimer = setTimeout(async () => {
        try {
          const predictions = await this._autocomplete(value);
          onPredictionsUpdated(predictions || []);
        } catch (error) {
          console.error('[HereRoutingStrategy] Destination autocomplete error:', error);
          onPredictionsUpdated([]);
        }
      }, 300);
    });

    inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });

    inputElement.focus();
  }

  async _autocomplete(input) {
    try {
      const url = `https://autocomplete.search.hereapi.com/v1/autocomplete`;
      const params = new URLSearchParams({
        q: input,
        apiKey: this.apiKey,
        country: 'IT,FR,CH'
      });

      const response = await fetch(`${url}?${params}`);
      const data = await response.json();

      console.log('[HereRoutingStrategy] Autocomplete response:', data);

      if (data.items && Array.isArray(data.items)) {
        return data.items;
      }

      return [];
    } catch (error) {
      console.error('[HereRoutingStrategy] Autocomplete request error:', error);
      throw error;
    }
  }

  buildDeepLink(waypoints, options = {}) {
    if (!Array.isArray(waypoints) || waypoints.length < 2) return null;

    const mode = options.truck ? 'tr' : 'd';

    const segment = (wp) => {
      const title = encodeURIComponent(wp.companyName || wp.address || '');
      if (wp.lat != null && wp.lng != null) {
        return `${wp.lat},${wp.lng},${title},s`;
      }
      return `${encodeURIComponent(wp.address || '')},${title},s`;
    };

    const path = waypoints.map(segment).join('/');

    return `https://share.here.com/r/${path}?m=${mode}`;
  }

  async selectPrediction(prediction) {
    try {
        if (!prediction) {
            return null;
        }

        const address =
            prediction.address?.label ||
            prediction.title ||
            '';

        if (!address) {
            console.warn(
                '[HereRoutingStrategy] selectPrediction: indirizzo non disponibile',
                prediction
            );

            return null;
        }

        let coords = null;

        if (
            Number.isFinite(Number(prediction.position?.lat)) &&
            Number.isFinite(Number(prediction.position?.lng))
        ) {
            coords = {
                lat: Number(prediction.position.lat),
                lng: Number(prediction.position.lng),
            };
        } else {
            coords = await this._geocodeAddress(address);
        }

        if (!coords) {
            return null;
        }

        return {
            label: prediction.title || address,
            address,
            lat: coords.lat,
            lng: coords.lng,
        };
    } catch (error) {
        console.error(
            '[HereRoutingStrategy] selectPrediction error:',
            error
        );

        return null;
    }
  }
}
